interface VisitCounterPayload {
  visits: number;
  counted?: boolean;
}

const VISITS_ENDPOINT = '/api/visits';
const REQUEST_TIMEOUT_MS = 3500;
const VISITOR_STORAGE_KEY = 'toonTankVisitorId';
const VISITOR_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const unavailableText = '--';

const formatter = new Intl.NumberFormat('zh-CN');

function visitCounterElements(): HTMLElement[] {
  return [...document.querySelectorAll<HTMLElement>('[data-visit-counter]')];
}

function setVisitCounterText(text: string, title: string): void {
  for (const element of visitCounterElements()) {
    element.textContent = text;
    element.title = title;
    element.dataset.state = text === unavailableText ? 'unavailable' : 'ready';
  }
}

function isValidVisitorId(value: unknown): value is string {
  return typeof value === 'string' && VISITOR_ID_PATTERN.test(value);
}

function createVisitorId(): string | null {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }

  if (typeof globalThis.crypto?.getRandomValues !== 'function') {
    return null;
  }

  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function readStoredVisitorId(): string | null {
  try {
    const visitorId = window.localStorage.getItem(VISITOR_STORAGE_KEY);

    if (isValidVisitorId(visitorId)) {
      return visitorId;
    }

    if (visitorId !== null) {
      window.localStorage.removeItem(VISITOR_STORAGE_KEY);
    }
  } catch {
    return null;
  }

  return null;
}

function getOrCreateVisitorId(): string | null {
  const storedVisitorId = readStoredVisitorId();

  if (storedVisitorId !== null) {
    return storedVisitorId;
  }

  const visitorId = createVisitorId();

  if (visitorId === null) {
    return null;
  }

  try {
    window.localStorage.setItem(VISITOR_STORAGE_KEY, visitorId);
  } catch {
    return null;
  }

  return visitorId;
}

function parseVisitCounterPayload(payload: unknown): VisitCounterPayload | null {
  if (
    typeof payload === 'object' &&
    payload !== null &&
    'visits' in payload &&
    typeof (payload as { visits: unknown }).visits === 'number' &&
    Number.isFinite((payload as { visits: number }).visits)
  ) {
    const counted = (payload as { counted?: unknown }).counted;

    return {
      visits: (payload as { visits: number }).visits,
      counted: typeof counted === 'boolean' ? counted : undefined
    };
  }

  return null;
}

async function fetchVisitCounter(): Promise<VisitCounterPayload> {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  const visitorId = getOrCreateVisitorId();
  const headers: Record<string, string> = {
    Accept: 'application/json'
  };
  const body = visitorId === null ? undefined : JSON.stringify({ visitorId });

  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  try {
    const response = await fetch(VISITS_ENDPOINT, {
      method: 'POST',
      headers,
      body,
      credentials: 'same-origin',
      signal: controller.signal
    });

    if (!response.ok) {
      throw new Error(`Visit counter returned HTTP ${response.status}`);
    }

    const payload = parseVisitCounterPayload(await response.json());

    if (payload === null) {
      throw new Error('Visit counter payload is invalid');
    }

    return payload;
  } finally {
    window.clearTimeout(timeoutId);
  }
}

export function syncVisitCounter(): void {
  if (visitCounterElements().length === 0) return;

  setVisitCounterText('统计中', '正在同步去重访问量');

  void fetchVisitCounter()
    .then(({ visits, counted }) => {
      setVisitCounterText(formatter.format(visits), `去重访问 ${formatter.format(visits)} 次`);
      window.dispatchEvent(
        new CustomEvent('toon-tank-visit-count', {
          detail: {
            visits,
            counted: counted ?? false
          }
        })
      );
    })
    .catch(() => {
      setVisitCounterText(unavailableText, '访问量统计暂不可用');
      window.dispatchEvent(new CustomEvent('toon-tank-visit-count-unavailable'));
    });
}
